/**
 * Helper functions to map API responses to GenericTable format
 */

/**
 * Map Materials & Consumption API response to table format
 * Response structure: { status, message, materialAndConsumptionList: [...] }
 * Dynamically render all fields from response
 */
export const mapMaterialAndConsumptionToTable = (apiResponse) => {
  // Handle different response structures
  let data = [];
  
  if (apiResponse?.materialAndConsumptionList) {
    // Direct structure
    data = apiResponse.materialAndConsumptionList;
  } else if (apiResponse?.data?.materialAndConsumptionList) {
    // Nested in data
    data = apiResponse.data.materialAndConsumptionList;
  } else if (apiResponse?.data?.data?.materialAndConsumptionList) {
    // Double nested
    data = apiResponse.data.data.materialAndConsumptionList;
  }
  
  console.log('[MaterialMapper] Mapping Materials & Consumption data:', { 
    dataCount: data.length, 
    sample: data[0],
    responseStructure: {
      hasDirectList: !!apiResponse?.materialAndConsumptionList,
      hasNestedList: !!apiResponse?.data?.materialAndConsumptionList,
      hasDoubleNested: !!apiResponse?.data?.data?.materialAndConsumptionList
    }
  });
  
  if (data.length === 0) {
    return { columns: [], rows: [] };
  }
  
  // Dynamically generate columns from the first item's keys
  const firstItem = data[0];
  const columnFieldMap = {
    collabrixBatchID: "Batch ID",
    procedureNumber: "Procedure Number",
    materialDescription: "Material Description",
    sponsorMaterialCode: "Sponsor Material Code",
    cmoMaterialCode: "CMO Material Code",
    vendorBatchNumber: "Vendor Batch Number",
    targetQuantity: "Target Quantity",
    actualQuantity: "Actual Quantity",
    uom: "UOM",
    // Add any other fields that might exist
  };
  
  // Create columns dynamically from all keys in the first item
  const columns = Object.keys(firstItem).map(key => ({
    id: key,
    name: columnFieldMap[key] || key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'), // Auto-format key name
    field: key
  }));
  
  // Map data rows with all fields from response
  const rows = data.map((item, index) => {
    const row = {
      id: item.collabrixBatchID + item.procedureNumber + (index.toString()), // Unique key for table
    };
    
    // Add all fields from the item
    Object.keys(item).forEach(key => {
      row[key] = item[key] !== undefined && item[key] !== null ? item[key] : "";
    });
    
    return row;
  });
  
  console.log('[MaterialMapper] Mapped Materials & Consumption:', { 
    columnsCount: columns.length, 
    rowsCount: rows.length,
    columns: columns.map(c => c.name)
  });
  
  return { columns, rows };
};

/**
 * Map CPP Details API response to table format
 * Response structure: { status, message, cppDetailsList: [...] }
 * Or nested: { status, data: { cppDetailsList: [...] } }
 */
export const mapCPPDetailsToTable = (apiResponse) => {
  // Handle different response structures
  // Structure 1: { status: true, cppDetailsList: [...] }
  // Structure 2: { status: true, data: { cppDetailsList: [...] } }
  // Structure 3: { data: { status: true, data: { cppDetailsList: [...] } } }
  let data = [];
  
  if (apiResponse?.cppDetailsList) {
    // Direct structure
    data = apiResponse.cppDetailsList;
  } else if (apiResponse?.data?.cppDetailsList) {
    // Nested in data
    data = apiResponse.data.cppDetailsList;
  } else if (apiResponse?.data?.data?.cppDetailsList) {
    // Double nested
    data = apiResponse.data.data.cppDetailsList;
  }
  
  console.log('[CPPMapper] Mapping CPP details data:', { 
    dataCount: data.length, 
    sample: data[0],
    responseStructure: {
      hasDirectList: !!apiResponse?.cppDetailsList,
      hasNestedList: !!apiResponse?.data?.cppDetailsList,
      hasDoubleNested: !!apiResponse?.data?.data?.cppDetailsList
    }
  });
  
  // Define columns dynamically based on the actual response structure
  const columns = [
    { id: 'collabrixCPPID', name: "CPP ID", field: "collabrixCPPID" },
    { id: 'procedureNumber', name: "Procedure Number", field: "procedureNumber" },
    { id: 'parameter', name: "Parameter", field: "parameter" },
    { id: 'setPoint', name: "Set Point", field: "setPoint" },
    { id: 'lowerLimit', name: "Lower Limit", field: "lowerLimit" },
    { id: 'upperLimit', name: "Upper Limit", field: "upperLimit" },
    { id: 'range', name: "Range", field: "range" }, // Computed field
    { id: 'actualValue', name: "Actual Value", field: "actualValue" },
    { id: 'uom', name: "UOM", field: "uom" },
  ];
  
  // Map data rows with all fields from response
  const rows = data.map((item, index) => {
    // Format range as "LowerLimit - UpperLimit"
    const range = item.lowerLimit !== undefined && item.upperLimit !== undefined 
      ? `${item.lowerLimit} - ${item.upperLimit}` 
      : "";
    
    return {
      id: item.collabrixCPPID || `cpp-${index}`, // Unique key for table
      collabrixBatchID: item.collabrixBatchID || "",
      collabrixCPPID: item.collabrixCPPID || "",
      procedureNumber: item.procedureNumber || "",
      parameter: item.parameter || "",
      setPoint: item.setPoint !== undefined && item.setPoint !== null ? item.setPoint : "",
      lowerLimit: item.lowerLimit !== undefined && item.lowerLimit !== null ? item.lowerLimit : "",
      upperLimit: item.upperLimit !== undefined && item.upperLimit !== null ? item.upperLimit : "",
      range: range,
      actualValue: item.actualValue !== undefined && item.actualValue !== null ? item.actualValue : "",
      uom: item.uom || "",
    };
  });
  
  console.log('[CPPMapper] Mapped CPP details:', { columnsCount: columns.length, rowsCount: rows.length });
  
  return { columns, rows };
};

/**
 * Convert tab name to array name dynamically
 * Examples: "Material & Consumption" -> "materialAndConsumptionList"
 *           "Batch Records" -> "batchRecordsList"
 *           "Quality Events" -> "qualityEventsList"
 */
const tabNameToArrayName = (tabName) => {
  // Handle special cases first
  const specialCases = {
    "Material & Consumption": "materialAndConsumptionList",
    "Materials & Consumption": "materialAndConsumptionList",
    "CPP Details": "cppDetailsList",
    "CPPs": "cppDetailsList",
    "In Process Test": "inProcessTestList",
    "In-Process Tests": "inProcessTestList",
    "Product Release Test": "productReleaseTestList",
    "Product Release Tests": "productReleaseTestList",
    "Deviations": "deviationList",
    "Change Controls": "changeControlList",
    "CC": "changeControlList",
    "Cycle Time": "cycleTimeList",
    "Equipment": "equipmentList",
    "Yield": "yieldList",
  };
  
  if (specialCases[tabName]) {
    return specialCases[tabName];
  }
  
  // Generic conversion: "Batch Records" -> "batchRecordsList"
  // Remove special characters, convert to camelCase, append "List"
  const cleaned = tabName
    .replace(/[&]/g, '') // Remove &
    .replace(/\s+/g, ' ') // Normalize spaces
    .trim();
  
  const words = cleaned.split(' ');
  const camelCase = words.map((word, index) => {
    if (index === 0) {
      return word.charAt(0).toLowerCase() + word.slice(1);
    }
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }).join('');
  
  return `${camelCase}List`;
};

/**
 * Helper: Get array name from tab name - Fully dynamic
 * Works for any tab name by converting it to camelCase + "List"
 */
const getArrayNameFromTabName = (tabName) => {
  return tabNameToArrayName(tabName);
};

/**
 * Generic mapper function that dynamically maps any tab response
 */
const mapGenericTabData = (apiResponse, arrayName, tabName) => {
  // Handle different response structures
  let data = [];
  
  if (apiResponse?.[arrayName]) {
    // Direct structure: { status: true, [arrayName]: [...] }
    data = apiResponse[arrayName];
  } else if (apiResponse?.data?.[arrayName]) {
    // Nested: { status: true, data: { [arrayName]: [...] } }
    data = apiResponse.data[arrayName];
  } else if (apiResponse?.data?.data?.[arrayName]) {
    // Double nested: { data: { status: true, data: { [arrayName]: [...] } } }
    data = apiResponse.data.data[arrayName];
  }
  
  console.log(`[${tabName}Mapper] Mapping ${tabName} data:`, { 
    dataCount: data.length, 
    sample: data[0],
    arrayName,
    responseStructure: {
      hasDirectList: !!apiResponse?.[arrayName],
      hasNestedList: !!apiResponse?.data?.[arrayName],
      hasDoubleNested: !!apiResponse?.data?.data?.[arrayName]
    }
  });
  
  if (data.length === 0) {
    return { columns: [], rows: [] };
  }
  
  // Helper function to convert camelCase to Title Case
  // Keeps acronyms together (no spaces between letters) and adds space before them
  const camelCaseToTitleCase = (str) => {
    if (!str) return '';
    
    // Define all common acronyms that should stay together
    // Order matters: longer acronyms first to avoid partial matches
    const acronyms = [
      'CPP', 'CQA', 'BMS', 'CMO', 'API', 'QC', 'ID', 'CC' // CC = Change Control
    ];
    
    // Create a map of acronyms to placeholders
    const acronymMap = {};
    const placeholderMap = {};
    acronyms.forEach((acronym, index) => {
      const placeholder = `__acronym${index}__`;
      acronymMap[acronym.toUpperCase()] = placeholder;
      placeholderMap[placeholder] = acronym;
    });
    
    // Replace acronyms with placeholders (case-insensitive, longer first)
    // Sort by length descending to match longer acronyms first
    const sortedAcronyms = [...acronyms].sort((a, b) => b.length - a.length);
    let temp = str;
    sortedAcronyms.forEach(acronym => {
      const regex = new RegExp(acronym, 'gi');
      temp = temp.replace(regex, acronymMap[acronym.toUpperCase()]);
    });
    
    // Convert camelCase to Title Case
    // This adds spaces before capital letters
    // But we need to handle placeholders specially - they should trigger space addition
    let result = temp.charAt(0).toUpperCase() + temp.slice(1);
    // Add space before capitals, but treat placeholders as if they start with a capital
    result = result.replace(/([a-z])([A-Z])/g, '$1 $2'); // Add space between lowercase and uppercase
    result = result.replace(/([a-z])(__acronym)/g, '$1 $2'); // Add space before placeholder if after lowercase
    
    // Restore acronyms as uppercase (in reverse order to maintain case)
    Object.keys(placeholderMap).sort((a, b) => b.length - a.length).forEach(placeholder => {
      const acronym = placeholderMap[placeholder];
      const regex = new RegExp(placeholder, 'gi');
      result = result.replace(regex, acronym);
    });
    
    // Ensure space before and after acronyms
    // This handles cases like "ChangeControlID" -> "Change Control ID"
    // Also handles "collabrixCPP" -> "Collabrix CPP"
    // And "CCNumber" -> "CC Number" (space after CC)
    sortedAcronyms.forEach(acronym => {
      // Add space BEFORE acronym if preceded by a letter (not already spaced)
      // Match any letter (lowercase or uppercase) followed directly by acronym without space
      const beforeRegex = new RegExp(`([a-zA-Z])(${acronym})(?=\\s|$|,|\\.|[A-Z])`, 'gi');
      result = result.replace(beforeRegex, '$1 $2');
      
      // Add space AFTER acronym if followed by a capital letter (start of next word)
      // This handles "CCNumber" -> "CC Number"
      const afterRegex = new RegExp(`(${acronym})([A-Z])`, 'gi');
      result = result.replace(afterRegex, '$1 $2');
    });
    
    // Clean up: remove multiple spaces and trim
    result = result.replace(/\s{2,}/g, ' ').trim();
    
    return result;
  };

  // Dynamically generate columns from the first item's keys
  const firstItem = data[0];
  const columnFieldMap = {
    // Common fields
    collabrixBatchID: "Batch ID",
    procedureNumber: "Procedure Number",
    operationNumber: "Operation Number",
    uom: "UoM",
    // In-Process Test specific
    operationDescription: "Operation Description",
    methodName: "Method Name",
    methodNumber: "Method Number",
    qcReceiptDate: "QC Receipt Date",
    inwardNumber: "Test Inward Number",
    arNumber: "Analytical Report Number",
    specificationLimit: "Specification Limit",
    resultValue: "Result Value",
    result: "Result",
    cqa: "CQA",
    // Equipment specific
    equipment: "Equipment ID",
    equipmentType: "Equipment Type",
    equipmentTitle: "Equipment Title",
    location: "Location",
    // Yield / Batch Yield specific
    plannedProduct: "Planned Product Quantity",
    actualProduct: "Actual Product Quantity",
    yieldPercentage: "Yield (%)",
    batchManufacturingDate: "Batch Manufacturing Date",
    expiryDate: "Expiry Date",
    storageCondition: "Storage Condition",
    storageLocation: "Storage Location",
  };

  // Fields to exclude from tab tables (internal IDs)
  const excludedFields = ["collabrixInProcessTestID", "collabrixProductReleaseTestID", "collabrixBatchID"];

  // Create columns dynamically from all keys in the first item
  const columns = Object.keys(firstItem)
    .filter(key => !excludedFields.includes(key))
    .map(key => ({
      id: key,
      name: columnFieldMap[key] || camelCaseToTitleCase(key), // Auto-format key name with proper acronym handling
      field: key
    }));

  // Flatten an API value to a renderable primitive.
  // Objects like { date, time } or { text, ... } or { name, ... } are unwrapped;
  // arrays are joined; everything else is returned as-is.
  const toRenderable = (val) => {
    if (val === null || val === undefined) return "";
    if (Array.isArray(val)) return val.join(", ");
    if (typeof val === "object") {
      if (val.date !== undefined) return val.date || "";
      if (val.text !== undefined) return val.text || "";
      if (val.name !== undefined) return val.name || "";
      return "";
    }
    return val;
  };

  // Map data rows with all fields from response
  const rows = data.map((item, index) => {
    const row = {
      id: item.collabrixInProcessTestID || item.collabrixProductReleaseTestID || item.collabrixBatchID || `row-${index}`, // Unique key for table
    };

    // Add all fields from the item
    Object.keys(item).forEach(key => {
      if (excludedFields.includes(key)) return;
      let value = toRenderable(item[key] !== undefined ? item[key] : null);
      // Operation Number: show only the value after the last "/"
      if (key === "operationNumber" && typeof value === "string" && value.includes("/")) {
        value = value.split("/").pop().trim();
      }
      row[key] = value;
    });

    return row;
  });
  
  console.log(`[${tabName}Mapper] Mapped ${tabName}:`, { 
    columnsCount: columns.length, 
    rowsCount: rows.length,
    columns: columns.map(c => c.name)
  });
  
  return { columns, rows };
};

/**
 * Map In-Process Test API response to table format
 * Response structure: { status, message, inProcessTestList: [...] }
 */
export const mapInProcessTestToTable = (apiResponse) => {
  return mapGenericTabData(apiResponse, "inProcessTestList", "InProcessTest");
};

/**
 * Map Product Release Test API response to table format
 * Response structure: { status, message, productReleaseTestList: [...] }
 */
export const mapProductReleaseTestToTable = (apiResponse) => {
  return mapGenericTabData(apiResponse, "productReleaseTestList", "ProductReleaseTest");
};

/**
 * Map Deviations API response to table format
 * Removed: collabrixDeviationID, deviationTitle, procedureNumber, initialDeviationNotification
 * Combined: CMO Deviation Status + Category + Type into one column
 * Combined: BMS Deviation ID + Status into one column
 * Added: viewMore for description/corrective/preventive, email timestamp columns
 */
export const mapDeviationsToTable = (apiResponse) => {
  const data = apiResponse?.deviationList || apiResponse?.data?.deviationList || [];

  const columns = [
    { name: "Deviation Number", field: "deviationNumber" },
    { name: "Reporting Date", field: "reportingDate" },
    { name: "Initial Deviation Description", field: "initialDescription" },
    { name: "Deviation Assessment Report", field: "assessmentReport" },
    { name: "Final Deviation Report", field: "finalReport" },
    { name: "Corrective Action", field: "correctiveAction" },
    { name: "CMO CA Status", field: "cmoCAStatus" },
    { name: "Preventive Action", field: "preventiveAction" },
    { name: "CMO PA Status", field: "cmoPAStatus" },
    { name: "Deviation Status", field: "deviationStatus" },
    { name: "Informed Sponsor", field: "informedSponsor" },
    { name: "Sponsor Deviation Status", field: "sponsorDeviationStatus" },
  ];

  const rows = data.map(item => ({
    deviationNumber: item.deviationNumber || "",
    reportingDate: item.reportingDate || "",
    initialDescription: item.initialDeviationDescription || item.initialDescription || "",
    assessmentReport: item.deviationAssessmentReport
      ? { name: item.deviationAssessmentReport, timestamp: item.deviationAssessmentReportTimestamp || "" }
      : (item.assessmentReport || { name: "", timestamp: "" }),
    finalReport: item.finalDeviationReport
      ? { name: item.finalDeviationReport, timestamp: item.finalDeviationReportTimestamp || "" }
      : (item.finalReport || { name: "", timestamp: "" }),
    correctiveAction: item.correctiveAction
      ? (typeof item.correctiveAction === 'string' ? { text: item.correctiveAction, caStatus: "" } : item.correctiveAction)
      : { text: "", caStatus: "" },
    cmoCAStatus: item.cmoCAStatus || "",
    preventiveAction: item.preventiveAction
      ? (typeof item.preventiveAction === 'string' ? { text: item.preventiveAction, paStatus: "" } : item.preventiveAction)
      : { text: "", paStatus: "" },
    cmoPAStatus: item.cmoPAStatus || "",
    deviationStatus: item.deviationStatus || item.cmoDeviationStatus || "",
    deviationCategory: item.deviationCategory || "",
    deviationType: item.deviationType || "",
    informedSponsor: item.informedSponsor || "",
    // Map existing bmsDeviationStatus/bmsDeviationID to Sponsor columns
    sponsorDeviationStatus: item.bmsDeviationStatus || item.sponsorDeviationStatus || "",
    sponsorDeviationId: item.bmsDeviationID || item.sponsorDeviationId || "",
  }));

  return { columns, rows };
};

/**
 * Map Change Controls API response to table format
 * Removed: collabrixChangeControlID, procedureNumber, ccTitle
 * ccType hardcoded to "Stage"
 */
export const mapChangeControlsToTable = (apiResponse) => {
  const data = apiResponse?.changeControlList || apiResponse?.data?.changeControlList || apiResponse?.data?.data?.changeControlList || [];

  const columns = [
    { name: "Change Control Type", field: "ccType" },
    { name: "Change Control Number", field: "ccNumber" },
    { name: "Initial Procedure Number", field: "initialProcedureNumber" },
    { name: "Final Procedure Number", field: "finalProcedureNumber" },
    { name: "Change From", field: "changeFrom" },
    { name: "Change To", field: "changeTo" },
    { name: "Reason For Change", field: "reasonForChange" },
    { name: "CMO Change Control Status", field: "cmoCCStatus" },
    { name: "Affected Batches", field: "affectedBatches" },
  ];

  const rows = data.map(item => ({
    ccType: "Stage",
    ccNumber: item.ccNumber || "",
    initialProcedureNumber: item.initialProcedureNumber || "",
    finalProcedureNumber: item.finalProcedureNumber || "",
    changeFrom: item.changeFrom || "",
    changeTo: item.changeTo || "",
    reasonForChange: item.reasonForChange || "",
    cmoCCStatus: item.cmoCCStatus || "",
    affectedBatches: item.affectedBatches || "",
  }));

  return { columns, rows };
};

/**
 * Map Cycle Time API response to table format
 * Response structure: { status, message, cycleTimeList: [...] }
 */
export const mapCycleTimeToTable = (apiResponse) => {
  return mapGenericTabData(apiResponse, "cycleTimeList", "CycleTime");
};

/**
 * Map Equipment API response to table format
 * Response structure: { status, message, equipmentList: [...] }
 */
export const mapEquipmentToTable = (apiResponse) => {
  return mapGenericTabData(apiResponse, "equipmentList", "Equipment");
};

/**
 * Map Yield API response to table format
 * Response structure: { status, message, yieldList: [...] }
 */
export const mapYieldToTable = (apiResponse) => {
  return mapGenericTabData(apiResponse, "yieldList", "Yield");
};

/**
 * Get mapper function for a tab name
 * Uses dedicated mappers for specific tabs, falls back to generic mapper
 */
export const getTabMapper = (tabName) => {
  // Dedicated mappers for specific tabs
  const dedicatedMappers = {
    "Deviations": mapDeviationsToTable,
    "Change Controls": mapChangeControlsToTable,
  };

  if (dedicatedMappers[tabName]) {
    return dedicatedMappers[tabName];
  }

  // Get the array name for this tab
  const arrayName = getArrayNameFromTabName(tabName);

  if (!arrayName) {
    console.warn(`[TabMapper] No array name found for tab: ${tabName}`);
    return null;
  }

  // Return a generic mapper function that uses the array name
  return (apiResponse) => {
    return mapGenericTabData(apiResponse, arrayName, tabName);
  };
};
